/**
 * Lolli Lifting — colourway switcher (swatches -> a lolli-range
 * section's panels).
 *
 * Two ways a swatch group can be wired up:
 *
 * 1. In-section (sections/lolli-range.liquid's own row): the group is
 *    inside the .lolli-range it controls — .closest('.lolli-range')
 *    finds it immediately, always. Never hidden; progressive
 *    enhancement only — without this script the buttons still render
 *    (aria-pressed already reflects the default colourway, set
 *    server-side) but clicking does nothing, leaving a plain static
 *    grid of the default colourway. Not a bug, the documented no-JS
 *    fallback.
 *
 * 2. Remote (sections/lolli-collection-hero.liquid, when its
 *    show_colourway_switcher setting is on): the group lives in a
 *    completely different Shopify section and is marked
 *    data-range-swatches-remote. .closest('.lolli-range') fails (it's
 *    not inside one), so instead: starting from the group's own
 *    .shopify-section wrapper, walk forward through sibling section
 *    wrappers (i.e. every top-level section on the page, in template
 *    order) until one containing a .lolli-range is found. If a page
 *    somehow has two lolli-range sections, only the nearest one that
 *    *follows* this swatch group is ever controlled — anything beyond
 *    it, or a range section placed before this group, is ignored.
 *    Remote groups render `hidden` (see the Liquid) and only get
 *    revealed here, once a target is confirmed to exist — a no-JS
 *    visitor, or a page with no matching range section at all, never
 *    sees a swatch row with nothing to control.
 *
 * Either way, every colourway already exists in the DOM (rendered by
 * snippets/lolli-range-panel.liquid); activating a swatch only ever
 * toggles the [hidden] attribute already present on the non-default
 * panels — no fetch, no re-render, no layout-affecting DOM insertion.
 */
(() => {
  const findRemoteRangeSection = (group) => {
    let wrapper = group.closest('.shopify-section');
    if (!wrapper) return null;
    wrapper = wrapper.nextElementSibling;
    while (wrapper) {
      const section = wrapper.querySelector('.lolli-range');
      if (section) return section;
      wrapper = wrapper.nextElementSibling;
    }
    return null;
  };

  document.querySelectorAll('[data-range-swatches]').forEach((group) => {
    const isRemote = group.hasAttribute('data-range-swatches-remote');
    const section = group.closest('.lolli-range') || (isRemote ? findRemoteRangeSection(group) : null);
    if (!section) return;

    const buttons = Array.from(group.querySelectorAll('[data-range-swatch]'));
    const panels = Array.from(section.querySelectorAll('[data-range-panel]'));
    if (!buttons.length || !panels.length) return;

    const activate = (swatch) => {
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.rangePanel !== swatch;
      });
      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.rangeSwatch === swatch));
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => activate(button.dataset.rangeSwatch));
    });

    if (isRemote) {
      // The row's server-rendered aria-pressed state is a same-tick
      // placeholder (see the comment in lolli-collection-hero.liquid)
      // — sync it to whichever panel the target section is actually
      // showing before ever revealing the row, so it can't disagree
      // with the section it controls.
      const activePanel = panels.find((panel) => !panel.hidden);
      if (activePanel) activate(activePanel.dataset.rangePanel);
      group.hidden = false;
    }
  });
})();
